import { ArrowRight, Check, ChevronDown, ChevronUp, Copy, Loader2, Mail } from 'lucide-react'
import { useRef, useState } from 'react'
import { useCreateTestApiKey, useTestApiKey } from '@/api/hooks/use-onboarding-key'
import { useSendSandboxEmail } from '@/api/hooks/use-sandbox'
import { useAuth } from '@/lib/auth-context'

interface StepSandboxSendProps {
  onNext: () => void
  onSetupDomain: () => void
}

const LANGUAGES = ['cURL', 'Node.js', 'Python', 'Go', 'Rust'] as const
type Language = (typeof LANGUAGES)[number]

function buildSnippets(apiKey: string, userEmail: string): Record<Language, string> {
  return {
    cURL: `curl -X POST https://api.one-email.com/v1/emails \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "sandbox@onboarding.one-email.com",
    "to": ["${userEmail}"],
    "subject": "Hello from One-Email",
    "html": "<p>Your first email, delivered.</p>"
  }'`,
    'Node.js': `import { OneEmail } from 'one-email'

const client = new OneEmail('${apiKey}')

await client.emails.send({
  from: 'sandbox@onboarding.one-email.com',
  to: ['${userEmail}'],
  subject: 'Hello from One-Email',
  html: '<p>Your first email, delivered.</p>'
})`,
    Python: `from one_email import OneEmail

client = OneEmail("${apiKey}")

client.emails.send(
    from_address="sandbox@onboarding.one-email.com",
    to=["${userEmail}"],
    subject="Hello from One-Email",
    html="<p>Your first email, delivered.</p>"
)`,
    Go: `package main

import oe "github.com/one-email/one-email-go"

func main() {
    client := oe.New("${apiKey}")

    client.Emails.Send(&oe.SendParams{
        From:    "sandbox@onboarding.one-email.com",
        To:      []string{"${userEmail}"},
        Subject: "Hello from One-Email",
        HTML:    "<p>Your first email, delivered.</p>",
    })
}`,
    Rust: `use one_email::{OneEmail, SendEmailRequest};

let client = OneEmail::new("${apiKey}").unwrap();

client.emails().send(SendEmailRequest {
    from: "sandbox@onboarding.one-email.com".into(),
    to: vec!["${userEmail}".into()],
    subject: "Hello from One-Email".into(),
    html: Some("<p>Your first email, delivered.</p>".into()),
    ..Default::default()
}).await?;`,
  }
}

function CopyBtn({ text }: { text: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false)

  function handleCopy(): void {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md p-1.5 text-text-tertiary hover:text-text-secondary hover:bg-surface-2 transition-colors"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

function buildWelcomeEmailHtml(recipientEmail: string): string {
  // Sanitize email to prevent HTML injection in the template
  const safeEmail = recipientEmail.replace(/[<>&"']/g, '')
  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1.0"/><meta name="color-scheme" content="light"/><title>Your first email, delivered</title>
<style>body,table,td,a{-webkit-text-size-adjust:100%}table,td{mso-table-lspace:0;mso-table-rspace:0}body{margin:0;padding:0;width:100%!important}@media only screen and (max-width:620px){.ec{width:100%!important;max-width:100%!important}.mp{padding-left:24px!important;padding-right:24px!important}}</style></head>
<body style="margin:0;padding:0;background-color:#FDF8F3;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif">
<div style="display:none;font-size:1px;line-height:1px;max-height:0;overflow:hidden">Your One-Email sandbox is live. You just sent your first email through our API.&#8199;&#65279;&#847;</div>
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#FDF8F3"><tr><td align="center" style="padding:40px 16px">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="560" class="ec" style="max-width:560px;background-color:#FEFCFA;border-radius:16px;border:1px solid #EDE9E4;overflow:hidden">
<tr><td style="padding:36px 40px 28px;text-align:center" class="mp">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center"><tr>
<td valign="middle" style="padding-right:12px">
<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style="display:block"><rect width="40" height="40" rx="3" fill="#FF6B00"/><rect x="3.8" y="3.8" width="8.5" height="6.2" rx="1.2" fill="#fff" opacity=".3"/><path d="M5.4 21.2L20 31.6L34.6 21.2" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><line x1="5.4" y1="13.1" x2="20" y2="13.1" stroke="#fff" stroke-width="1.2" stroke-linecap="round" opacity=".4"/><line x1="5.4" y1="16.9" x2="13.8" y2="16.9" stroke="#fff" stroke-width=".9" stroke-linecap="round" opacity=".25"/></svg>
</td>
<td valign="middle"><span style="font-size:20px;font-weight:800;color:#1a1216;letter-spacing:-.5px">one-email</span></td>
</tr></table></td></tr>
<tr><td style="padding:0 40px" class="mp"><div style="height:2px;background:linear-gradient(90deg,#FF6B00,#FF9E4F,#FFD1AD);border-radius:2px"></div></td></tr>
<tr><td style="padding:36px 40px 0;text-align:center" class="mp">
<div style="width:56px;height:56px;border-radius:50%;background-color:#FFF5ED;border:2px solid #FFD1AD;margin:0 auto 20px;line-height:56px;text-align:center"><span style="font-size:26px;line-height:56px">&#10003;</span></div>
<h1 style="margin:0;font-size:28px;font-weight:800;color:#1a1216;letter-spacing:-.5px;line-height:1.2">Your first email, delivered</h1>
</td></tr>
<tr><td style="padding:20px 40px 0;text-align:center" class="mp">
<p style="margin:0 0 18px;font-size:16px;line-height:1.65;color:#5a5452">If you're reading this, your sandbox is working. This email was sent through the One-Email API — the same infrastructure that will handle your production traffic.</p>
<p style="margin:0 0 18px;font-size:16px;line-height:1.65;color:#5a5452">One-Email is a developer email platform built on our own SMTP servers. Auto IP warming, real-time analytics, and deliverability monitoring — with a free tier of 10,000 emails/month.</p>
</td></tr>
<tr><td style="padding:8px 40px 0" class="mp">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#FFF5ED;border-radius:12px;border:1px solid #FFE8D6"><tr><td style="padding:22px 24px">
<p style="margin:0 0 12px;font-size:13px;font-weight:700;color:#B84D00;text-transform:uppercase;letter-spacing:.04em">Next steps</p>
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
<tr><td style="padding:4px 0;font-size:15px;line-height:1.6;color:#1a1216"><span style="color:#FF6B00;font-weight:700;margin-right:8px">1.</span> Add and verify your sending domain</td></tr>
<tr><td style="padding:4px 0;font-size:15px;line-height:1.6;color:#1a1216"><span style="color:#FF6B00;font-weight:700;margin-right:8px">2.</span> Install the SDK: <code style="font-size:13px;background:#FEFCFA;border:1px solid #EDE9E4;border-radius:4px;padding:2px 6px;color:#B84D00">npm i one-email</code></td></tr>
<tr><td style="padding:4px 0;font-size:15px;line-height:1.6;color:#1a1216"><span style="color:#FF6B00;font-weight:700;margin-right:8px">3.</span> Send your first production email</td></tr>
</table></td></tr></table></td></tr>
<tr><td style="padding:28px 40px 0;text-align:center" class="mp">
<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center"><tr>
<td style="border-radius:10px;background-color:#FF6B00"><a href="https://app.one-email.com" target="_blank" style="display:inline-block;padding:14px 32px;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;border-radius:10px">Open your dashboard &rarr;</a></td>
</tr></table></td></tr>
<tr><td style="padding:36px 0 0">&nbsp;</td></tr>
<tr><td style="padding:20px 40px 28px;border-top:1px solid #EDE9E4;text-align:center" class="mp">
<p style="margin:0 0 6px;font-size:13px;color:#9a9290;line-height:1.5">Sent to ${safeEmail} via <strong style="color:#5a5452">One-Email</strong> — fast, reliable email for developers</p>
<p style="margin:0;font-size:12px;color:#9a9290;line-height:1.5">This is a sandbox test email from your onboarding flow.<br/><a href="https://one-email.com" style="color:#FF6B00;text-decoration:none">one-email.com</a></p>
</td></tr>
</table></td></tr></table></body></html>`
}

export function StepSandboxSend({
  onNext,
  onSetupDomain,
}: StepSandboxSendProps): React.JSX.Element {
  const { user } = useAuth()
  const userEmail = user?.email ?? ''

  const [apiKey, setApiKey] = useState<string | null>(null)
  const [showCode, setShowCode] = useState(false)
  const [lang, setLang] = useState<Language>('cURL')

  const { data: testKey, isLoading: isLoadingKey } = useTestApiKey()
  const createTestKey = useCreateTestApiKey()
  const sendEmail = useSendSandboxEmail()

  const sent = sendEmail.isSuccess

  const hasTriggeredCreate = useRef(false)

  // If a test key exists and has the full key (post-migration), use it directly
  if (!apiKey && testKey?.key?.startsWith('oe_test_sk_') && !testKey.key.includes('...')) {
    setApiKey(testKey.key)
  }

  // Sync from mutation result
  if (!apiKey && createTestKey.data?.key) {
    setApiKey(createTestKey.data.key)
  }

  // Create a test key if none exists, or if existing key is masked (pre-migration)
  const needsNewKey =
    !isLoadingKey &&
    !apiKey &&
    !createTestKey.isPending &&
    !createTestKey.data &&
    !hasTriggeredCreate.current &&
    (testKey === null || (testKey?.key?.includes('...') ?? false))

  if (needsNewKey && !createTestKey.isError) {
    hasTriggeredCreate.current = true
    createTestKey.mutateAsync().then((result) => {
      setApiKey(result.key)
    }).catch(() => {
      hasTriggeredCreate.current = false
    })
  }

  function handleSendEmail(): void {
    if (!apiKey) return
    sendEmail.mutate({
      apiKey,
      to: userEmail,
      subject: 'Your first email, delivered',
      html: buildWelcomeEmailHtml(userEmail),
    })
  }

  const isSettingUp = isLoadingKey || createTestKey.isPending
  const snippets = buildSnippets(apiKey ?? 'oe_test_sk_...', userEmail)

  /* ── Success state ─────────────────────────────────────────── */

  if (sent) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Email sent!</h2>
            <p className="text-sm text-text-secondary">
              Check <span className="font-medium text-text-primary">{userEmail}</span> — it should
              arrive in a few seconds.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3">
          <p className="text-sm font-medium text-brand-700">
            Ready to send from your own domain?{' '}
            <button type="button" onClick={onSetupDomain} className="underline hover:no-underline">
              Set up a domain
            </button>{' '}
            to unlock all recipients and remove sandbox limits.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onNext}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  /* ── Main view ─────────────────────────────────────────────── */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50">
          <Mail className="h-5 w-5 text-brand-500" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Send your first email</h2>
          <p className="text-sm text-text-secondary">
            We&apos;ll send a test email to{' '}
            <span className="font-medium text-text-primary">{userEmail}</span>
          </p>
        </div>
      </div>

      {/* Test API key */}
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-tertiary">
          Your test API key
        </p>
        <div className="flex items-center justify-between rounded-lg border border-surface-3 bg-surface-1 px-4 py-3">
          {isSettingUp ? (
            <span className="flex items-center gap-2 text-sm text-text-tertiary">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Creating key...
            </span>
          ) : (
            <code className="font-mono text-sm text-text-primary break-all">
              {apiKey ?? 'oe_test_sk_...'}
            </code>
          )}
          {apiKey && <CopyBtn text={apiKey} />}
        </div>
        <p className="mt-1.5 text-xs text-text-tertiary">
          This key is always available in Settings &rarr; API Keys.
        </p>
      </div>

      {/* Email preview */}
      <div className="rounded-xl border border-surface-3 bg-surface-1 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-surface-3 bg-surface-0">
          <div className="flex items-center gap-2 text-xs text-text-tertiary">
            <span className="font-medium text-text-secondary">To:</span>
            <span>{userEmail}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-tertiary mt-1">
            <span className="font-medium text-text-secondary">Subject:</span>
            <span>Your first email, delivered</span>
          </div>
        </div>
        <div className="px-4 py-3 text-sm text-text-primary">
          <p className="font-semibold">Your first email, delivered</p>
          <p className="mt-1 text-text-secondary">
            If you&apos;re reading this, your sandbox is working. This email was sent through the
            One-Email API.
          </p>
        </div>
      </div>

      {/* Send button */}
      <button
        type="button"
        onClick={handleSendEmail}
        disabled={isSettingUp || sendEmail.isPending}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-3.5 text-sm font-semibold text-white
          hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
          shadow-[0_4px_16px_rgba(255,107,0,0.2)]"
      >
        {sendEmail.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Mail className="h-4 w-4" />
            Send test email
          </>
        )}
      </button>

      {sendEmail.isError && (
        <p className="text-sm text-red-600">
          {sendEmail.error instanceof Error
            ? sendEmail.error.message
            : 'Failed to send. Try again.'}
        </p>
      )}

      {/* Code reference (collapsible) */}
      <div className="border-t border-surface-3 pt-4">
        <button
          type="button"
          onClick={() => setShowCode(!showCode)}
          className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          {showCode ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {showCode ? 'Hide code' : 'Or send via code'}
        </button>

        {showCode && (
          <div className="mt-4 space-y-3">
            {/* Language tabs */}
            <div className="flex gap-1 rounded-lg bg-surface-1 p-1 border border-surface-3">
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                    l === lang
                      ? 'bg-surface-0 text-text-primary shadow-sm'
                      : 'text-text-tertiary hover:text-text-secondary'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            {/* Code snippet */}
            <div className="relative rounded-lg border border-surface-3 bg-surface-0">
              <div className="absolute right-2 top-2">
                <CopyBtn text={snippets[lang]} />
              </div>
              <pre className="overflow-x-auto p-4 font-mono text-xs text-text-primary leading-relaxed">
                {snippets[lang]}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Skip */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onNext}
          className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
        >
          Skip for now
        </button>
        <p className="text-xs text-text-tertiary">Sandbox: sends only to your account email</p>
      </div>
    </div>
  )
}
